import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, IconButton, Button, Chip, Paper,
  CircularProgress, Divider, Select, MenuItem, TextField,
  Popover, Checkbox,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import MapIcon from '@mui/icons-material/Map';
import TableRowsIcon from '@mui/icons-material/TableRows';
import HubIcon from '@mui/icons-material/Hub';
import SettingsIcon from '@mui/icons-material/Settings';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import LagbasLogo from '../../../../components/LagbasLogo';
import HeroBg from '../components/HeroBg';
import { db } from '../../../../firebase/config';
import {
  collection, doc, getDoc, getDocs, query, orderBy,
  onSnapshot, updateDoc, Timestamp,
} from 'firebase/firestore';
import { Toaster, toast } from 'sonner';
import { MAPPN_ACCENT, STATUS_OPTIONS, IMPL_OPTIONS, TECH_TYPES } from '../constants';
import ComponentDetail from '../components/ComponentDetail';

const ALL_COLS = [
  { key: 'title',          label: 'Titel',          always: true  },
  { key: 'status',         label: 'Status',         always: false },
  { key: 'category',       label: 'Kategori',       always: false },
  { key: 'perspectives',   label: 'Perspektiv',     always: false },
  { key: 'implementation', label: 'Implementation', always: false },
  { key: 'features',       label: 'Funktioner',     always: false },
  { key: 'tech',           label: 'Tech stack',     always: false },
  { key: 'comments',       label: 'Kommentarer',    always: false },
  { key: 'notes',          label: 'Anteckningar',   always: false },
];

const DEFAULT_COLS = ['title', 'status', 'category', 'perspectives', 'implementation'];

function lsKey(caseId) { return `mappn_table_cols_${caseId}`; }

export default function MappNTable() {
  const { caseId } = useParams();
  const navigate = useNavigate();

  const [caseData, setCaseData] = useState(null);
  const [perspectives, setPerspectives] = useState([]);
  const [categories, setCategories] = useState([]);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [visibleCols, setVisibleCols] = useState(() => {
    try { return JSON.parse(localStorage.getItem(lsKey(caseId))) || DEFAULT_COLS; }
    catch { return DEFAULT_COLS; }
  });
  const [colAnchor, setColAnchor] = useState(null);
  const [editing, setEditing] = useState(null); // { compId, col }
  const [editVal, setEditVal] = useState('');

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detailData, setDetailData] = useState(null);

  useEffect(() => {
    let unsub;
    async function init() {
      setLoading(true);
      const [cSnap, pSnap, catSnap] = await Promise.all([
        getDoc(doc(db, 'mappnCases', caseId)),
        getDocs(query(collection(db, 'mappnCases', caseId, 'perspectives'), orderBy('order'))),
        getDocs(query(collection(db, 'mappnCases', caseId, 'categories'), orderBy('order'))),
      ]);
      if (!cSnap.exists()) { navigate('/applab/mappn'); return; }
      setCaseData({ id: cSnap.id, ...cSnap.data() });
      setPerspectives(pSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setCategories(catSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      unsub = onSnapshot(
        query(collection(db, 'mappnCases', caseId, 'components'), orderBy('createdAt', 'desc')),
        snap => {
          setComponents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
          setLoading(false);
        }
      );
    }
    init();
    return () => unsub?.();
  }, [caseId, navigate]);

  function toggleCol(key) {
    setVisibleCols(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      localStorage.setItem(lsKey(caseId), JSON.stringify(next));
      return next;
    });
  }

  const cols = ALL_COLS.filter(c => c.always || visibleCols.includes(c.key));

  async function saveField(compId, field, value) {
    try {
      await updateDoc(doc(db, 'mappnCases', caseId, 'components', compId), {
        [field]: value, updatedAt: Timestamp.now(),
      });
    } catch {
      toast.error('Kunde inte spara');
    }
    setEditing(null);
  }

  function openNew() {
    setDetailId('__new__');
    setDetailData(null);
    setDetailOpen(true);
  }

  function openDetail(comp) {
    setDetailId(comp.id);
    setDetailData(comp);
    setDetailOpen(true);
  }

  function handleSaved(savedId, savedData) {
    setComponents(prev => {
      const exists = prev.find(c => c.id === savedId);
      if (exists) return prev.map(c => c.id === savedId ? { ...c, ...savedData } : c);
      return [{ id: savedId, ...savedData }, ...prev];
    });
    if (detailId === '__new__') { setDetailId(savedId); setDetailData(savedData); }
  }

  function handleDeleted(deletedId) {
    setComponents(prev => prev.filter(c => c.id !== deletedId));
    setDetailOpen(false);
  }

  const TABS = [
    { label: 'Kort',          path: 'cards',        icon: <ViewModuleIcon sx={{ fontSize: '0.9rem' }} /> },
    { label: 'Perspektiv',    path: 'perspectives', icon: <ViewColumnIcon sx={{ fontSize: '0.9rem' }} /> },
    { label: 'Karta',         path: 'map',          icon: <MapIcon        sx={{ fontSize: '0.9rem' }} /> },
    { label: 'Tabell',        path: 'table',        icon: <TableRowsIcon  sx={{ fontSize: '0.9rem' }} /> },
    { label: 'Tech',          path: 'tech',         icon: <HubIcon        sx={{ fontSize: '0.9rem' }} /> },
    { label: 'Inställningar', path: 'settings',     icon: <SettingsIcon   sx={{ fontSize: '0.9rem' }} /> },
  ];

  if (loading && !caseData) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#0d1117', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: MAPPN_ACCENT }} />
      </Box>
    );
  }

  const hasHero = !!(caseData?.heroVideoUrl || caseData?.heroImageUrl);

  return (
    <>
    <HeroBg caseData={caseData} />
    <Box sx={{ minHeight: '100vh', bgcolor: hasHero ? 'transparent' : '#0d1117', color: 'white', display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1 }}>
      <Toaster richColors position="top-center" />

      {/* Header */}
      <Box sx={{
        bgcolor:        hasHero ? 'rgba(13,17,23,0.72)' : '#161b22',
        backdropFilter: hasHero ? 'blur(22px) saturate(1.3)' : 'none',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        py: 1.5, px: 3, display: 'flex', alignItems: 'center', gap: 2,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <IconButton color="inherit" size="small" onClick={() => navigate('/applab/mappn')}>
          <ArrowBackIcon />
        </IconButton>
        {caseData?.heroImageUrl && (
          <Box component="img" src={caseData.heroImageUrl}
            sx={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>{caseData?.name || '…'}</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
            {components.length} komponenter
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {TABS.map(tab => (
            <Button
              key={tab.path}
              startIcon={tab.icon}
              onClick={() => navigate(`/applab/mappn/${caseId}/${tab.path}`)}
              sx={{
                textTransform: 'none', fontSize: '0.8rem',
                color: tab.path === 'table' ? MAPPN_ACCENT : 'rgba(255,255,255,0.5)',
                bgcolor: tab.path === 'table' ? MAPPN_ACCENT + '18' : 'transparent',
                borderRadius: 1,
                '&:hover': { bgcolor: MAPPN_ACCENT + '22' },
              }}
            >
              {tab.label}
            </Button>
          ))}
        </Box>
        <Button
          variant="contained" startIcon={<AddIcon />}
          onClick={openNew}
          sx={{ bgcolor: MAPPN_ACCENT, '&:hover': { bgcolor: '#1976D2' }, textTransform: 'none', fontWeight: 700, ml: 1 }}
        >
          Ny komponent
        </Button>
      </Box>

      {/* Toolbar */}
      <Box sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <Button
          startIcon={<ViewWeekIcon sx={{ fontSize: '0.9rem' }} />}
          onClick={e => setColAnchor(e.currentTarget)}
          size="small"
          sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', '&:hover': { color: 'white' } }}
        >
          Kolumner
        </Button>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)' }}>
          Klicka i en cell för att redigera · Klicka på titeln för att öppna detaljer
        </Typography>
      </Box>

      {/* Column chooser */}
      <Popover
        open={Boolean(colAnchor)}
        anchorEl={colAnchor}
        onClose={() => setColAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { bgcolor: '#1a2030', border: '1px solid rgba(255,255,255,0.1)', p: 2, minWidth: 200 } }}
      >
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Välj kolumner
        </Typography>
        {ALL_COLS.filter(c => !c.always).map(col => (
          <Box key={col.key} sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
            <Checkbox
              checked={visibleCols.includes(col.key)}
              onChange={() => toggleCol(col.key)}
              size="small"
              sx={{ color: 'rgba(255,255,255,0.3)', '&.Mui-checked': { color: MAPPN_ACCENT }, p: 0.75 }}
            />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem' }}>
              {col.label}
            </Typography>
          </Box>
        ))}
      </Popover>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress sx={{ color: MAPPN_ACCENT }} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
          <Paper sx={{ bgcolor: '#161b22', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 2, overflow: 'hidden' }}>
            {/* Header row */}
            <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)', bgcolor: '#0d1117' }}>
              {cols.map(col => (
                <Box key={col.key} sx={{ ...colWidthSx(col.key), fontWeight: 700, fontSize: '0.7rem', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.06em', py: 1.5, px: 1.5 }}>
                  {col.label}
                </Box>
              ))}
            </Box>

            {/* Rows */}
            {components.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.25)' }}>Inga komponenter ännu</Typography>
              </Box>
            ) : (
              components.map((comp, idx) => (
                <Box
                  key={comp.id}
                  sx={{
                    display: 'flex', alignItems: 'stretch',
                    borderBottom: idx < components.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.015)' },
                  }}
                >
                  {cols.map(col => (
                    <CellRenderer
                      key={col.key}
                      col={col.key}
                      comp={comp}
                      perspectives={perspectives}
                      categories={categories}
                      isEditing={editing?.compId === comp.id && editing?.col === col.key}
                      editVal={editVal}
                      setEditVal={setEditVal}
                      onStartEdit={val => { setEditing({ compId: comp.id, col: col.key }); setEditVal(val ?? ''); }}
                      onSave={val => saveField(comp.id, colToField(col.key), val)}
                      onCancel={() => setEditing(null)}
                      onOpenDetail={() => openDetail(comp)}
                      onSavePerspectives={ids => saveField(comp.id, 'perspectiveIds', ids)}
                    />
                  ))}
                </Box>
              ))
            )}
          </Paper>
        </Box>
      )}

      <ComponentDetail
        open={detailOpen}
        caseId={caseId}
        componentId={detailId}
        initialData={detailData}
        perspectives={perspectives}
        categories={categories}
        caseTechStack={caseData?.techStack || []}
        onClose={() => setDetailOpen(false)}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
      />
    </Box>
    </>
  );
}

function colWidthSx(key) {
  const widths = {
    title: 220, status: 120, category: 120, perspectives: 200,
    implementation: 130, estimate: 100, features: 90, tech: 200,
    comments: 100, notes: 260,
  };
  const w = widths[key] || 140;
  return {
    width: w,
    flexShrink: 0,
    flexGrow: (key === 'title' || key === 'notes') ? 1 : 0,
  };
}

function colToField(col) {
  const map = { category: 'categoryId', perspectives: 'perspectiveIds' };
  return map[col] || col;
}

function CellRenderer({
  col, comp, perspectives, categories,
  isEditing, editVal, setEditVal,
  onStartEdit, onSave, onCancel, onOpenDetail, onSavePerspectives,
}) {
  const baseSx = { ...colWidthSx(col), px: 1.5, py: 1, display: 'flex', alignItems: 'center', minHeight: 42 };

  if (col === 'title') {
    if (isEditing) {
      return (
        <Box sx={baseSx}>
          <TextField
            autoFocus value={editVal}
            onChange={e => setEditVal(e.target.value)}
            onBlur={() => onSave(editVal)}
            onKeyDown={e => { if (e.key === 'Enter') onSave(editVal); if (e.key === 'Escape') onCancel(); }}
            size="small" fullWidth
            sx={{
              '& .MuiInputBase-input': { color: 'white', fontSize: '0.82rem', py: 0.5 },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: MAPPN_ACCENT },
            }}
          />
        </Box>
      );
    }
    return (
      <Box sx={{ ...baseSx, gap: 1 }}>
        {comp.avatarUrl && (
          <Box component="img" src={comp.avatarUrl}
            sx={{ width: 22, height: 22, borderRadius: 0.5, objectFit: 'cover', flexShrink: 0 }} />
        )}
        <Typography
          sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'white', flex: 1, cursor: 'pointer', '&:hover': { color: MAPPN_ACCENT } }}
          onClick={onOpenDetail}
        >
          {comp.title || '(Namnlös)'}
        </Typography>
        <Typography
          onClick={() => onStartEdit(comp.title || '')}
          sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.2)', cursor: 'text', flexShrink: 0, '&:hover': { color: 'rgba(255,255,255,0.5)' } }}
        >
          redigera
        </Typography>
      </Box>
    );
  }

  if (col === 'status') {
    const s = STATUS_OPTIONS.find(x => x.key === comp.status);
    return (
      <Box sx={baseSx}>
        <Select
          value={comp.status || ''}
          onChange={e => onSave(e.target.value)}
          size="small" displayEmpty
          sx={{
            fontSize: '0.75rem', color: s?.color || 'rgba(255,255,255,0.25)',
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.3)', fontSize: '1rem' },
            bgcolor: s ? s.color + '18' : 'transparent', borderRadius: 1, fontWeight: s ? 700 : 400,
          }}
          renderValue={v => {
            const o = STATUS_OPTIONS.find(x => x.key === v);
            return o ? o.label : <em style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem' }}>–</em>;
          }}
        >
          <MenuItem value=""><em>–</em></MenuItem>
          {STATUS_OPTIONS.map(o => (
            <MenuItem key={o.key} value={o.key} sx={{ fontSize: '0.8rem' }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: o.color, mr: 1, display: 'inline-block' }} />
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  }

  if (col === 'category') {
    const cat = categories.find(c => c.id === comp.categoryId);
    return (
      <Box sx={baseSx}>
        <Select
          value={comp.categoryId || ''}
          onChange={e => onSave(e.target.value)}
          size="small" displayEmpty
          sx={{
            fontSize: '0.75rem', color: cat?.color || 'rgba(255,255,255,0.25)',
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.3)', fontSize: '1rem' },
            bgcolor: cat ? cat.color + '18' : 'transparent', borderRadius: 1,
          }}
          renderValue={v => {
            const c = categories.find(x => x.id === v);
            return c ? c.name : <em style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem' }}>–</em>;
          }}
        >
          <MenuItem value=""><em>–</em></MenuItem>
          {categories.map(c => (
            <MenuItem key={c.id} value={c.id} sx={{ fontSize: '0.8rem' }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c.color, mr: 1, display: 'inline-block' }} />
              {c.name}
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  }

  if (col === 'perspectives') {
    const ids = comp.perspectiveIds || [];
    return (
      <Box sx={{ ...baseSx, flexWrap: 'wrap', gap: 0.4 }}>
        {perspectives.map(p => {
          const active = ids.includes(p.id);
          return (
            <Chip
              key={p.id} label={p.name} size="small"
              onClick={() => onSavePerspectives(active ? ids.filter(id => id !== p.id) : [...ids, p.id])}
              sx={{
                bgcolor: active ? p.color + '33' : 'rgba(255,255,255,0.04)',
                color: active ? p.color : 'rgba(255,255,255,0.3)',
                border: `1px solid ${active ? p.color + '55' : 'transparent'}`,
                fontSize: '0.62rem', height: 18, cursor: 'pointer',
                '&:hover': { bgcolor: p.color + '22' },
              }}
            />
          );
        })}
        {perspectives.length === 0 && <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.2)' }}>–</Typography>}
      </Box>
    );
  }

  if (col === 'implementation') {
    const impl = IMPL_OPTIONS.find(o => o.key === comp.implementation);
    return (
      <Box sx={baseSx}>
        <Select
          value={comp.implementation || ''}
          onChange={e => onSave(e.target.value)}
          size="small" displayEmpty
          sx={{
            fontSize: '0.75rem', color: impl?.color || 'rgba(255,255,255,0.25)',
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.3)', fontSize: '1rem' },
            bgcolor: impl ? impl.color + '18' : 'transparent', borderRadius: 1,
          }}
          renderValue={v => {
            const o = IMPL_OPTIONS.find(x => x.key === v);
            return o ? o.label : <em style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem' }}>–</em>;
          }}
        >
          <MenuItem value=""><em>–</em></MenuItem>
          {IMPL_OPTIONS.map(o => (
            <MenuItem key={o.key} value={o.key} sx={{ fontSize: '0.8rem' }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: o.color, mr: 1, display: 'inline-block' }} />
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  }

  if (col === 'features') {
    const count = (comp.features || []).length;
    return (
      <Box sx={baseSx}>
        <Typography sx={{ fontSize: '0.75rem', color: count ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.2)' }}>
          {count || '–'}
        </Typography>
      </Box>
    );
  }

  if (col === 'tech') {
    const tech = comp.techStack || [];
    return (
      <Box sx={{ ...baseSx, flexWrap: 'wrap', gap: 0.4 }}>
        {tech.slice(0, 4).map(t => {
          const tt = TECH_TYPES.find(x => x.key === t.type) || TECH_TYPES[7];
          return (
            <Chip key={t.id} label={t.name} size="small" sx={{
              bgcolor: tt.bg + '44', color: 'rgba(255,255,255,0.65)', fontSize: '0.6rem', height: 16,
            }} />
          );
        })}
        {tech.length > 4 && (
          <Chip label={`+${tech.length - 4}`} size="small" sx={{
            bgcolor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.3)', fontSize: '0.6rem', height: 16,
          }} />
        )}
        {tech.length === 0 && <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.2)' }}>–</Typography>}
      </Box>
    );
  }

  if (col === 'comments') {
    const total = comp.commentCount || 0;
    const unresolved = comp.unresolvedCommentCount || 0;
    return (
      <Box sx={baseSx}>
        <Typography sx={{ fontSize: '0.75rem', color: unresolved ? MAPPN_ACCENT + 'cc' : 'rgba(255,255,255,0.2)' }}>
          {total ? `${unresolved}/${total}` : '–'}
        </Typography>
      </Box>
    );
  }

  if (col === 'notes') {
    if (isEditing) {
      return (
        <Box sx={baseSx}>
          <TextField
            autoFocus multiline maxRows={3}
            value={editVal}
            onChange={e => setEditVal(e.target.value)}
            onBlur={() => onSave(editVal)}
            onKeyDown={e => { if (e.key === 'Escape') onCancel(); }}
            size="small" fullWidth
            sx={{
              '& .MuiInputBase-input': { color: 'white', fontSize: '0.78rem', py: 0.5 },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: MAPPN_ACCENT },
            }}
          />
        </Box>
      );
    }
    return (
      <Box sx={{ ...baseSx, cursor: 'text' }} onClick={() => onStartEdit(comp.notes || '')}>
        <Typography sx={{
          fontSize: '0.75rem',
          color: comp.notes ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.12)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {comp.notes || 'Klicka för att redigera'}
        </Typography>
      </Box>
    );
  }

  return <Box sx={baseSx} />;
}
